/**
 * Wrapper mỏng quanh `usePreferenceColumns` cho Futures Control tabs.
 *
 * Trước đây dùng `useDatasheetColumns` + endpoint legacy
 * `/futures-control/preferences/columns/{mode}` (FLAT shape). Giờ chuyển sang
 * generic preference registry — `(scope='futures', key='columns', subKey=<mode>)`
 * với nested shape `{columnOrder, columnVisible, sortBy}`.
 *
 * Baseline rows seeded bởi migration `0016_futures_baseline_seed.sql`.
 * Spec: `backend/app/services/preferences/baselines/futures_columns.py`.
 *
 * Call sites giữ nguyên signature `useFuturesColumns({ mode, allColumns, storageKey? })`.
 * `storageKey` còn để compat — KHÔNG còn dùng (LocalStorage đã retire), giữ làm
 * `widthsTableKey` để `resetToDefault` cũng wipe widths của user cho table tương ứng.
 */
import {
  usePreferenceColumns,
  type UsePreferenceColumnsParams,
} from '../../utils/datasheetColumns/usePreferenceColumns'
import type {
  DatasheetColumnLike,
  UseDatasheetColumnsMenuResult,
} from '../../utils/datasheetColumns/types'

/** FE mode strings cho 11 list-style Futures tabs — match baseline keys BE. */
export type FuturesColumnsMode =
  | 'orders'
  | 'spreads'
  | 'allocation'
  | 'allocation-noalc'
  | 'market'
  | 'pucontract'
  | 'sacontract'
  | 'bank-cash'
  | 'bank-statement'
  | 'adjust-lot'
  | 'bank-cost-change'

export type FuturesColumnLike = DatasheetColumnLike
export type UseFuturesColumnsResult<TCol extends DatasheetColumnLike> =
  UseDatasheetColumnsMenuResult<TCol>

export interface UseFuturesColumnsParams<TCol extends DatasheetColumnLike> {
  mode: FuturesColumnsMode
  allColumns: TCol[]
  /**
   * Optional table_key (e.g. `'futures.orders'`) — passed as `widthsTableKey`
   * to `usePreferenceColumns` so "Default columns" cũng wipe widths của user.
   */
  storageKey?: string
}

export function useFuturesColumns<TCol extends DatasheetColumnLike>(
  params: UseFuturesColumnsParams<TCol>,
): UseDatasheetColumnsMenuResult<TCol> {
  const cfg: UsePreferenceColumnsParams<TCol> = {
    scope: 'futures',
    prefKey: 'columns',
    subKey: params.mode,
    allColumns: params.allColumns,
    widthsTableKey: params.storageKey,
  }
  return usePreferenceColumns<TCol>(cfg)
}
